import {AbstractControl, AbstractControlOptions, FormBuilder, FormGroup} from "@angular/forms";
import {FormValueChangeItem} from "./form-value-change.item";
import {FormMetadata} from "./form-metadata";
import {FormSelectOption} from "./form-select-option";
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  Observable,
  pairwise,
  startWith,
  Subject,
  Subscription
} from "rxjs";
import {filter} from "rxjs/operators";
import {WrapperDto} from "../../../repositories/dto/wrapper.dto";
import {ValidationErrorDto} from "../../../repositories/dto/validation-error.dto";
import {HttpErrorResponse} from "@angular/common/http";
import {ExceptionErrorDto} from "../../../repositories/dto/exception-error.dto";
import {isDefined, isTrue, nonBlank, notDefined, unsubscribe} from "../../utils/epic.utils";
import {LoggingService} from "../../../services/logger/logging.service";
import {FormStateEnum} from "./form-state.enum";
import {FormOptions} from "./form-options";

export class FormWrapper {

  public static readonly VALIDATION_FIELD_GLOBAL = 'object';

  public readonly id: string;

  private group!: FormGroup;
  private metadata: FormMetadata[] = [];
  private ready: boolean = false;
  private buildReady: boolean = false;
  private metadataReady: boolean = false;
  private exceptions: ExceptionErrorDto[] = [];
  private validationErrors: ValidationErrorDto[] = [];
  private state: FormStateEnum = FormStateEnum.EMPTY;
  private onSubmitFunction: ((value: any) => Observable<any>) | undefined;
  private autoSubmit: boolean;

  private readonly options: FormOptions;
  private readonly autoCleanErrors: boolean;
  private readonly onChangeEvent: Subject<FormValueChangeItem> = new Subject<FormValueChangeItem>();
  private readonly onPatchEvent: Subject<FormValueChangeItem> = new Subject<FormValueChangeItem>();
  private readonly onReadyEvent: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.ready);
  private readonly onStateChangeEvent: Subject<FormStateEnum> = new Subject<FormStateEnum>();
  private readonly onSubmitEvent: Subject<any> = new Subject<any>();
  private readonly onPreSubmitEvent: Subject<any> = new Subject<any>();
  private readonly onSubmitProcessEvent: Subject<any> = new Subject<any>();
  private readonly onAfterSubmitEvent: Subject<any> = new Subject<any>();
  private readonly subs: Subscription[] = [];
  private readonly subWrappers: FormWrapper[] = [];

  constructor(id: string, private log: LoggingService, private builder: FormBuilder, options?: FormOptions) {
    this.id = id;
    this.options = isDefined(options) ? options : new FormOptions();
    this.autoSubmit = this.options.autoSubmit;
    this.autoCleanErrors = this.options.autoCleanErrors;
    this.setState(FormStateEnum.NONE);
  }

  public destroy(): void {
    this.buildReady = false;
    this.metadataReady = false;
    this.setReady();
    this.exceptions = [];
    this.validationErrors = [];
    this.metadata.forEach((m) => m.destroy());
    this.getSubWrappers().forEach((w) => w.destroy());
    unsubscribe(this.subs);
  }

  public build(controls: object, metadata: FormMetadata[], options?: AbstractControlOptions | null): void {
    this.group = this.builder.group(controls, this.prepareOptions(options));
    this.metadata = this.prepareMetadata(metadata);
    this.subs.push(
      this.onSubmit().subscribe(() => {
        this.processOnSubmit();
        this.onAfterSubmitEvent.next(true);
      }),
      forkJoin(this.metadata.map((m) => m.loadSelectOptions())).subscribe(() => {
        this.metadataReady = true;
        this.setReady();
      }));
    this.init();
    this.postBuild();
    this.buildReady = true;
    this.setReady();
  }

  // ##########################################################################
  // #### Public
  // ##########################################################################

  public getId(): string {
    return this.id;
  }

  public submit(touchInvalid: boolean = false, cleanErrors: boolean = false): void {
    if (isTrue(cleanErrors)) {
      this.cleanErrors();
    }
    if (isTrue(touchInvalid)) {
      this.markAllInvalidAsTouched();
    }
    this.onPreSubmitEvent.next(null);
    setTimeout(() => this.onSubmitEvent.next(this.form.value), 100);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public valueChanged(name: string, newValue: any, oldValue: any, initialValue: boolean = false): void {
    this.onChangeEvent.next(new FormValueChangeItem(name, newValue, oldValue, initialValue, 'change'));
    if (!initialValue) {
      this.updateState(FormStateEnum.CHANGED);
      this.cleanErrors();
    }
    this.updateInfo(name, newValue);
    this.updateWarn(name, newValue);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public valuePatched(name: string, newValue: any, oldValue: any, initialValue: boolean = false): void {
    this.onPatchEvent.next(new FormValueChangeItem(name, newValue, oldValue, initialValue, 'patch'));
  }

  public getTranslationCode(name: string): string {
    const metadata: FormMetadata | undefined = this.getMetadataByName(name);
    return isDefined(metadata) ? metadata.translation : name;
  }

  public getSelectOptions(name: string): FormSelectOption[] {
    const metadata: FormMetadata | undefined = this.getMetadataByName(name);
    return isDefined(metadata) ? metadata.selectOptions : [];
  }

  public getDefaultOption(name: string): FormSelectOption | undefined {
    const metadata: FormMetadata | undefined = this.getMetadataByName(name);
    return isDefined(metadata) ? metadata.defaultOption : undefined;
  }

  public hasAsyncSelectOptions(name: string): boolean {
    const metadata: FormMetadata | undefined = this.getMetadataByName(name);
    return isDefined(metadata) && isDefined(metadata.asyncSelectOptions);
  }

  public getAsyncSelectOptions(name: string): ((input: any) => Observable<FormSelectOption[]>) | undefined {
    const metadata: FormMetadata | undefined = this.getMetadataByName(name);
    return isDefined(metadata) ? metadata.asyncSelectOptions : undefined;
  }

  public hasAsyncSelectOptionById(name: string): boolean {
    const metadata: FormMetadata | undefined = this.getMetadataByName(name);
    return isDefined(metadata) && isDefined(metadata.asyncSelectOptionById);
  }

  public getAsyncSelectOptionById(name: string): ((id: string) => Observable<FormSelectOption | undefined>) | undefined {
    const metadata: FormMetadata | undefined = this.getMetadataByName(name);
    return isDefined(metadata) ? metadata.asyncSelectOptionById : undefined;
  }

  public updateState(state: FormStateEnum): void {
    this.setState(state);
  }

  public cleanErrors(): void {
    if (this.autoCleanErrors) {
      this.exceptions = [];
      this.validationErrors = [];
    }
    this.getSubWrappers().forEach((w) => w.cleanErrors());
  }

  public addSubWrapper(subWrapper: FormWrapper): void {
    this.subWrappers.push(subWrapper);
  }

  public getSubWrappers(): FormWrapper[] {
    return isDefined(this.subWrappers) ? this.subWrappers : [];
  }

  public isValid(): boolean {
    return this.form.valid && this.getSubWrappers().every((w) => w.isValid());
  }

  public isInvalid(): boolean {
    return !this.isValid();
  }

  public getMetadataByName(name: string): FormMetadata | undefined {
    return this.metadata.find((m) => m.name === name);
  }

  public markAllInvalidAsTouched(): void {
    Object.keys(this.form.controls).forEach((name) => {
      const control: AbstractControl | null = this.form.get(name);
      if (isDefined(control) && control.invalid) {
        control.markAsTouched();
      }
    });
    this.getSubWrappers().forEach((w) => w.markAllInvalidAsTouched());
  }

  // ##########################################################################
  // #### Private
  // ##########################################################################

  // see https://stackoverflow.com/a/58832436
  private init(): void {
    Object.keys(this.form.controls).forEach((name) => {
      const control: AbstractControl = this.form.controls[name];
      this.subs.push(control.valueChanges.pipe(
        startWith(control.value), // it's necessary to send a first value
        pairwise(), // the pairwise make the 'magic'
        filter(([oldValue, newValue]) => oldValue != newValue)
      ).subscribe(([oldValue, newValue]) => {
        this.valueChanged(name, newValue, oldValue);
      }));
      // Initial set of info/help/warn for labels
      this.updateInfo(name, this.getValue(name));
      this.updateWarn(name, this.getValue(name));
    });
  }

  private prepareOptions(options?: AbstractControlOptions | null): AbstractControlOptions {
    let opts: AbstractControlOptions | null | undefined = options;
    if (isDefined(opts)) {
      opts.updateOn = 'blur';
    } else {
      opts = {updateOn: 'blur'};
    }
    return opts;
  }

  private postBuild(): void {
    if (this.isViewOnly()) {
      this.autoSubmit = false;
      Object.keys(this.form.controls).forEach((key: string) => this.form.controls[key].disable());
    }
  }

  private prepareMetadata(metadata: FormMetadata[]): FormMetadata[] {
    return metadata;
  }

  private setState(state: FormStateEnum): void {
    this.state = state;
    this.onStateChangeEvent.next(state);
  }

  private processOnSubmit(): void {
    if (this.isInvalid()) {
      this.setState(FormStateEnum.CHANGED);
      if (this.options.markAllAsTouchedOnInvalidSubmit) {
        this.form.markAllAsTouched();
      }
    } else if (this.isValid() && this.onSubmitFunction) {
      this.setState(FormStateEnum.WORKING);
      this.log.debug('FormWrapper submit: {}', [this.form.value]);
      this.subs.push(this.onSubmitFunction(this.form.value).subscribe({
        next: (value: WrapperDto<any>) => {
          this.setState(FormStateEnum.SUCCESS);
          this.onSubmitProcessEvent.next(value);
        },
        error: (ex: HttpErrorResponse) => {
          this.parseError(ex);
          this.onSubmitProcessEvent.error(ex);
        },
        complete: () => this.onSubmitProcessEvent.complete()
      }));
    }
  }

  public parseError(ex: HttpErrorResponse | WrapperDto<any>, fieldName?: string): void {
    this.setState(FormStateEnum.ERROR);
    const err: any = ex instanceof HttpErrorResponse ? ex.error : ex;
    if (isDefined(err) && isDefined(err.dataType)) {
      if (err.dataType === 'ValidationErrorDto') {
        this.parseValidationError(new WrapperDto<ValidationErrorDto>(err), fieldName);
      } else if (err.dataType === 'ExceptionErrorDto') {
        this.parseExceptionError(new WrapperDto<ExceptionErrorDto>(err));
      } else {
        this.log.error('Unhandled form error datatype "{}"', [err.dataType]);
      }
    } else if (ex instanceof HttpErrorResponse) {
      this.parseHttpError(ex);
    } else {
      this.log.error('Unhandled http error type "{}"', [ex]);
    }
  }

  private parseValidationError(wrapperDto: WrapperDto<ValidationErrorDto>, fieldName?: string): void {
    this.validationErrors = [];
    const errors: ValidationErrorDto[] = wrapperDto.getErrors();
    if (errors.length > 0) {
      const targetField: string | undefined = isDefined(fieldName) && nonBlank(fieldName) && this.hasControl(fieldName) ? fieldName : undefined;
      const fieldsErrors: Record<string, any> = {};
      errors.forEach((err) => {
        if (notDefined(fieldsErrors[err.field])) {
          fieldsErrors[err.field] = [];
        }
        fieldsErrors[err.field].push({['api' + err.errorCode]: err.messageCode});

        // A field this form does not have (e.g. a rejected attachment) shows with the global errors instead of vanishing
        const unknownField: boolean = notDefined(targetField) && !this.hasControl(err.field);
        if (fieldName === FormWrapper.VALIDATION_FIELD_GLOBAL || err.field === FormWrapper.VALIDATION_FIELD_GLOBAL || unknownField) {
          this.validationErrors.push(err);
        }
      });

      if (isDefined(targetField)) {
        Object.keys(fieldsErrors)
          .filter((key) => fieldsErrors[key].length > 0)
          .forEach((key) => {
            const control: AbstractControl = this.getControl(targetField);
            control.setErrors(fieldsErrors[key], {emitEvent: true});
            control.markAsTouched();
            this.log.debug('Set error by field name "{}": {}', [targetField, fieldsErrors[key]]);
          });
      } else {
        Object.keys(fieldsErrors)
          .filter((key) => fieldsErrors[key].length > 0)
          .filter((key) => this.hasControl(key))
          .forEach((key) => {
            const control: AbstractControl = this.getControl(key);
            control.setErrors(fieldsErrors[key], {emitEvent: true});
            control.markAsTouched();
            this.log.debug('Set error by key "{}": {}', [key, fieldsErrors[key]]);
          });
      }
    }
  }

  private parseExceptionError(wrapperDto: WrapperDto<ExceptionErrorDto>): void {
    this.exceptions = wrapperDto.getErrors();
  }

  private parseHttpError(ex: HttpErrorResponse): void {
    const msg: string = (ex.status ? ex.status + ' - ' : '') + ex.message;
    switch (ex.status) {
      case 404:
        this.exceptions = [ExceptionErrorDto.of('90404', 'exception.notFound', msg)];
        break;
      default: {
        const code: string = '90' + (ex.status ? ex.status : '000');
        this.exceptions = [ExceptionErrorDto.of(code, 'exception.unknown', msg)];
      }
    }
  }

  private setReady(): void {
    this.ready = this.buildReady && this.metadataReady;
    this.onReadyEvent.next(this.ready);
  }

  public updateInfo(name: string, value: string): void {
    const meta: FormMetadata | undefined = this.getMetadataByName(name);
    if (isDefined(meta)) {
      meta.updateInfo(value);
    }
  }

  public updateWarn(name: string, value: string): void {
    const meta: FormMetadata | undefined = this.getMetadataByName(name);
    if (isDefined(meta)) {
      meta.updateWarn(value);
    }
  }

  // ##########################################################################
  // #### Getter and Setter
  // ##########################################################################

  public get form(): FormGroup {
    return this.group;
  }

  public hasControl(field: string): boolean {
    return !!(this.form.controls[field]);
  }

  public getControl(field: string): AbstractControl {
    return this.form.controls[field];
  }

  public getValue(field: string): any {
    return this.getControl(field).value;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public setValue(field: string, value: any): void {
    if (this.getControl(field)) {
      const control: AbstractControl = this.getControl(field);
      control.markAsDirty();
      control.setValue(value);
    } else {
      this.log.error('Value cannot be set, field "{}" does not exist', [field]);
    }
  }

  // datas: [field, value][]
  public patchValues(datas: [string, any][]): void {
    datas.forEach(([f, v]) => this.patchValue(f, v));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public patchValue(field: string, value: any): void {
    if (this.hasControl(field)) {
      const oldValue: any = this.getValue(field);
      this.getControl(field).patchValue(value, {emitEvent: false});
      this.valuePatched(field, value, oldValue);
    } else {
      this.log.error('Value cannot be patched, field "{}" does not exist', [field]);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public patchDiffValue(field: string, value: any): void {
    const current: any = this.getValue(field);
    if (current !== value) {
      this.patchValue(field, value);
    }
  }

  public isDirty(): boolean {
    const sw: FormWrapper[] = this.getSubWrappers();
    if (sw.length > 0 && sw.find((w) => w.isDirty())) {
      return true;
    }
    return this.getDirtyFields().length > 0;
  }

  public getDirtyFields(): string[] {
    const dirtyFields: string[] = [];
    if (isDefined(this.form)) {
      Object.keys(this.form.controls).forEach((key) => {
        if (this.form.controls[key].dirty) {
          dirtyFields.push(key);
        }
      });
    }
    return dirtyFields;
  }

  public markAllAsPristine(): void {
    this.getDirtyFields().forEach((field) => this.getControl(field).markAsPristine());
    this.getSubWrappers().forEach((sw) => sw.markAllAsPristine());
  }

  public onSubmit(): Observable<any> {
    return this.onSubmitEvent.asObservable();
  }

  // noinspection JSUnusedGlobalSymbols
  public onPreSubmit(): Observable<any> {
    return this.onPreSubmitEvent.asObservable();
  }

  public onAfterSubmit(): Observable<any> {
    return this.onAfterSubmitEvent.asObservable();
  }

  public onSubmitFn<T>(fn: (value: T) => Observable<any>): Observable<any> {
    this.onSubmitFunction = fn;
    return this.onSubmitProcessEvent.asObservable();
  }

  public onChange(): Observable<FormValueChangeItem> {
    return this.onChangeEvent.asObservable();
  }

  public onChangeDelayed(): Observable<FormValueChangeItem> {
    return this.onChange().pipe(debounceTime(this.options.changeDelayTime));
  }

  public onChangeDelayedByName(name: string): Observable<FormValueChangeItem> {
    return this.onChangeDelayed().pipe(filter((v) => v.getName() === name));
  }

  public onChangeDistinctNameValue(): Observable<FormValueChangeItem> {
    return this.onChange().pipe(distinctUntilChanged((p, c) => p.getName() === c.getName() && p.getValue() === c.getValue()));
  }

  public onChangeByName(name: string): Observable<FormValueChangeItem> {
    return this.onChange().pipe(filter((v) => v.getName() === name));
  }

  public onChangeByNameDistinctValue(name: string): Observable<FormValueChangeItem> {
    return this.onChangeByName(name).pipe(distinctUntilChanged((p, c) => p.getValue() === c.getValue()));
  }

  public onPatch(): Observable<FormValueChangeItem> {
    return this.onPatchEvent.asObservable();
  }

  public onPatchByName(name: string): Observable<FormValueChangeItem> {
    return this.onPatch().pipe(filter((v) => v.getName() === name));
  }

  public onPatchByNameDistinctValue(name: string): Observable<FormValueChangeItem> {
    return this.onPatchByName(name).pipe(distinctUntilChanged((p, c) => p.getValue() === c.getValue()));
  }

  public getState(): FormStateEnum {
    if (isDefined(this.options.fixedFormState)) {
      return this.options.fixedFormState;
    }
    return this.state;
  }

  public isStateOk(): boolean {
    return this.getState() === FormStateEnum.NONE || this.getState() === FormStateEnum.SUCCESS;
  }

  public onStateChange(): Observable<FormStateEnum> {
    return this.onStateChangeEvent.asObservable();
  }

  public get isReady(): boolean {
    return this.ready;
  }

  public get notReady(): boolean {
    return !this.isReady;
  }

  public onReady(): Observable<boolean> {
    return this.onReadyEvent.asObservable().pipe(filter((ready: boolean): boolean => ready));
  }

  public hasExceptions(): boolean {
    return this.exceptions.length > 0;
  }

  public getExceptions(): ExceptionErrorDto[] {
    return this.exceptions;
  }

  public hasValidationErrors(): boolean {
    return this.validationErrors.length > 0;
  }

  public getValidationErrors(): ValidationErrorDto[] {
    return this.validationErrors;
  }

  public addValidationError(error: ValidationErrorDto): void {
    this.validationErrors.push(error);
  }

  public hasInfo(name: string): boolean {
    const meta: FormMetadata | undefined = this.getMetadataByName(name);
    //this.updateInfo(name, this.getValue(name));
    return !!(meta && meta.hasInfo());
  }

  public getInfo(name: string): string {
    const meta: FormMetadata | undefined = this.getMetadataByName(name);
    return isDefined(meta) && nonBlank(meta.info) ? meta.info! : '';
  }

  public isInfoTranslated(name: string): boolean {
    const meta: FormMetadata | undefined = this.getMetadataByName(name);
    return isDefined(meta) && meta.isInfoTranslated();
  }

  public hasHelp(name: string): boolean {
    return nonBlank(this.getHelp(name));
  }

  public getHelp(name: string): string {
    const meta: FormMetadata | undefined = this.getMetadataByName(name);
    return isDefined(meta) && nonBlank(meta.help) ? meta.help! : '';
  }

  public hasWarn(name: string): boolean {
    const meta: FormMetadata | undefined = this.getMetadataByName(name);
    //this.updateWarn(name, this.getValue(name));
    return !!(meta && meta.hasWarn());
  }

  public getWarn(name: string): string {
    const meta: FormMetadata | undefined = this.getMetadataByName(name);
    return isDefined(meta) && nonBlank(meta.warn) ? meta.warn! : '';
  }

  public isAutoSubmit(): boolean {
    return this.autoSubmit;
  }

  public setAutoSubmit(autoSubmit: boolean): void {
    this.autoSubmit = autoSubmit;
  }

  public isViewOnly(name?: string): boolean {
    if (this.options.viewOnly) {
      return true;
    } else if (nonBlank(name) && isDefined(this.form) && Object.prototype.hasOwnProperty.call(this.form.controls, name!)) {
      return this.form.controls[name!].disabled;
    }
    return false;
  }

  public getChangeDelayTime(): number {
    return this.options.changeDelayTime;
  }
}
